import { locateDatasets } from './dataset-location';

/**
 * KML Import/Export Utilities
 * Handles KML file format for geospatial data
 */

/**
 * Parse KML file to extract features
 */
export async function parseKML(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const kmlText = e.target.result;
        const parser = new DOMParser();
        const kmlDoc = parser.parseFromString(kmlText, 'text/xml');
        
        // Extract placemarks
        const placemarks = kmlDoc.getElementsByTagName('Placemark');
        const features = [];
        
        for (let i = 0; i < placemarks.length; i++) {
          const placemark = placemarks[i];
          const name = placemark.getElementsByTagName('name')[0]?.textContent || 'Unnamed';
          const description = placemark.getElementsByTagName('description')[0]?.textContent || '';
          
          // Get coordinates
          const coordinates = placemark.getElementsByTagName('coordinates')[0]?.textContent;
          if (coordinates) {
            const coords = coordinates.trim().split(',');
            if (coords.length >= 2) {
              features.push({
                name,
                description,
                longitude: parseFloat(coords[0]),
                latitude: parseFloat(coords[1]),
                elevation: coords.length > 2 ? parseFloat(coords[2]) : 0
              });
            }
          }
        }
        
        resolve(features);
      } catch (error) {
        reject(new Error('Failed to parse KML: ' + error.message));
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

/**
 * Export datasets to KML format
 */
export function exportToKML(datasets, filename = 'datasets.kml') {
  const kmlHeader = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>Ultimate GIS Datasets</name>
    <description>Exported geophysical datasets</description>`;

  const { placed, skipped } = locateDatasets(datasets);
  const placemarks = placed
    .map(({ dataset, lon, lat }) => `
    <Placemark>
      <name>${escapeXml(dataset.name)}</name>
      <description>
        <![CDATA[
          <b>Method:</b> ${dataset.method_type}<br/>
          <b>QC Status:</b> ${dataset.qc_status}<br/>
          <b>Created:</b> ${new Date(dataset.created_at).toLocaleDateString()}
        ]]>
      </description>
      <Point>
        <coordinates>${lon},${lat},0</coordinates>
      </Point>
    </Placemark>`)
    .join('');

  const kmlFooter = `
  </Document>
</kml>`;

  if (!placed.length) return { exported: 0, skipped };
  const kmlContent = kmlHeader + placemarks + kmlFooter;

  // Download
  const blob = new Blob([kmlContent], { type: 'application/vnd.google-earth.kml+xml' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return { exported: placed.length, skipped };
}

function escapeXml(unsafe) {
  return String(unsafe ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Geodesic distance calculation (Haversine formula)
 */
export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371e3; // Earth radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) *
    Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

/**
 * Convert between coordinate systems
 */
export function convertCoordinates(lat, lon, fromSystem = 'WGS84', toSystem = 'WGS84') {
  // Placeholder for coordinate system conversion
  // In production, use proj4js library
  
  if (fromSystem === toSystem) {
    return { lat, lon };
  }
  
  // TODO: Implement coordinate transforms with proj4js
  console.warn('Coordinate system conversion not yet implemented');
  return { lat, lon };
}
